import { createAsyncThunk } from '@reduxjs/toolkit';
import HttpError from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import {
  returnAxiosOrGeneralFetchError,
  returnAxiosValidationOrGeneralFetchError,
} from '../../services/data/axios-base';
import Organization from '../../models/organization/organization';
import organizationsDataService from '../../services/data/organizations-data-service';
import User, { UserData } from '../../models/general/user';
import ListOrganization, {
  OrganizationTreeNode,
} from '../../models/organization/list-organization';
import FormOrganization from '../../models/organization/form-organization';
import FormUser from '../../models/general/form-user';
import Filters from '@common/enums/query-params/filters';
import UserPatchType from '../../models/enums/user-patch-type';
import DeploymentWeeks from '../../models/organization/deployment-week';
import ExternalApi, { PostApiKey } from '../../models/organization/external-api';
import { SearchResponse } from '../../models/misc/search-result';
import SearchType from '../../models/enums/search';
import OrganizationDeleteData from '../../models/organization/organization-delete-data';
import OrganizationRole from '../../models/enums/organization-roles';

export const getOrganizationById = createAsyncThunk<
  Organization,
  string,
  { rejectValue: HttpError }
>('organizations/get', async (id: string, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getOrganizationById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getAllOrganizationsById = createAsyncThunk<
  ListOrganization[],
  Filters,
  { rejectValue: HttpError }
>('organizations/get-all', async (filters: Filters, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getAllOrganizationsById(filters);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getOrganizationTreeById = createAsyncThunk<
  OrganizationTreeNode[],
  string,
  { rejectValue: HttpError }
>('organizations/get-tree', async (org: string, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getOrganizationTree(org);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getLocationSetById = createAsyncThunk<
  ListOrganization[],
  string,
  { rejectValue: HttpError }
>('organizations/get-location-set', async (id, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getLocationSetById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getOrganizationDeleteDataById = createAsyncThunk<
  OrganizationDeleteData,
  string,
  { rejectValue: HttpError }
>('organizations/get-delete', async (id, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getOrganizationDeleteDataById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createSubOrganization = createAsyncThunk<
  ListOrganization,
  Partial<FormOrganization>,
  { rejectValue: HttpError | ValidationResult[] }
>('organizations/post', async (data: Partial<FormOrganization>, { rejectWithValue }) => {
  try {
    return await organizationsDataService.createSubOrganization(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const createUser = createAsyncThunk<
  User,
  Partial<FormUser>,
  { rejectValue: HttpError | ValidationResult[] }
>('users/post', async (data: Partial<FormUser>, { rejectWithValue }) => {
  try {
    return await organizationsDataService.createUser(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchUserById = createAsyncThunk<
  User,
  { id: string; data: Partial<FormUser>; patchType: UserPatchType },
  { rejectValue: HttpError | ValidationResult[] }
>('users/patch', async (data: { id: string; data: Partial<FormUser> }, { rejectWithValue }) => {
  try {
    return await organizationsDataService.patchUserById(data.id, data.data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getOrganizationUsersById = createAsyncThunk<
  User[],
  Filters,
  { rejectValue: HttpError }
>('users/get', async (filters: Filters, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getOrganizationUsersById(filters);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getUserById = createAsyncThunk<User, string, { rejectValue: HttpError }>(
  'users/retrieve',
  async (id, { rejectWithValue }) => {
    try {
      return await organizationsDataService.getUserById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const deleteUserById = createAsyncThunk<
  string,
  { id: string; deletingSelf: boolean },
  { rejectValue: HttpError }
>('users/delete', async (data: { id: string; deletingSelf: boolean }, { rejectWithValue }) => {
  try {
    return await organizationsDataService.deleteUserById(data.id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getAllUserDataById = createAsyncThunk<UserData, string, { rejectValue: HttpError }>(
  'user-data/retrieve',
  async (id, { rejectWithValue }) => {
    try {
      return await organizationsDataService.getAllUserDataById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const patchOrganizationById = createAsyncThunk<
  Organization,
  { id: string; data: Partial<FormOrganization> },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'organizations/patch',
  async (data: { id: string; data: Partial<FormOrganization> }, { rejectWithValue }) => {
    try {
      return await organizationsDataService.patchOrganizationById(data.id, data.data);
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const deleteOrganizationById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'organizations/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      return await organizationsDataService.deleteOrganizationById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getDeploymentWeeksById = createAsyncThunk<
  DeploymentWeeks[],
  { id: string; filters: Filters },
  { rejectValue: HttpError }
>('organizations/get-deployment-weeks', async ({ id, filters }, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getDeploymentWeeksById(id, filters);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getSearchResults = createAsyncThunk<
  SearchResponse,
  { search: string; fields: SearchType[]; childOrgId?: string; orgRole?: OrganizationRole },
  { rejectValue: HttpError }
>('search', async (payload, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getSearch(payload);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getApiKeysByOrganizationId = createAsyncThunk<
  ExternalApi[],
  string,
  { rejectValue: HttpError }
>('api-key/get', async (organizationId: string, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getApiKey(organizationId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createApiKey = createAsyncThunk<
  ExternalApi,
  PostApiKey,
  { rejectValue: HttpError | ValidationResult[] }
>('api-key/post', async (payload, { rejectWithValue }) => {
  try {
    return await organizationsDataService.createApiKey(payload);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteApiKeyById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'api-key/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      return await organizationsDataService.deleteApiKeyById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);
